import { useCallback, useEffect, useState } from "react";
import {
  Alert, Badge, Button, Card, Col, DatePicker, Descriptions, Empty, Form, Input,
  InputNumber, Row, Select, Space, Statistic, Table, Tag, Tooltip, Typography, message,
} from "antd";
import {
  CarOutlined, CheckCircleOutlined, ClockCircleOutlined, EnvironmentOutlined,
  ExclamationCircleOutlined, ExpandAltOutlined, SearchOutlined, StopOutlined,
  SyncOutlined, WarningOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import { useNavigate } from "react-router-dom";
import { transportApi } from "../../api/transportApi";
import { getDefaultContext } from "../../api/httpClient";
import { getEnteredByUser } from "../../utils/currentUser";
import { getApiErrorMessage } from "../../utils/apiError";
import { useEWBNotifications } from "../../hooks/useEWBNotifications";
import type { EWayBillListItemDto } from "../../types/api";
import { StaticModal } from "../../shared/components/StaticModal";
import { SaveActionButton } from "../../shared/components/SaveActionButton";

dayjs.extend(relativeTime);

interface EWBDashboardStats {
  totalActive: number;
  expiringIn8Hours: number;
  extensionWindowOpen: number;
  manualActionRequired: number;
  delivered: number;
  cancelled: number;
  expired: number;
  autoStoppedToday: number;
}

interface EWBEnvironment {
  isSandbox: boolean;
  environment: string;
  baseUrl: string;
}

/** NIC extension reason codes */
const EXTENSION_REASON_OPTIONS = [
  { value: 1, label: "1 – Natural Calamity / Accident" },
  { value: 2, label: "2 – Law & Order Situation" },
  { value: 3, label: "3 – Transhipment" },
  { value: 4, label: "4 – Other (default)" },
  { value: 5, label: "5 – In Transit Cargo" },
  { value: 6, label: "6 – Vehicle Breakdown" },
  { value: 7, label: "7 – Strike / Bandh" },
  { value: 8, label: "8 – Flood / Heavy Rain" },
];

const STATUS_COLORS: Record<string, string> = {
  Generated: "blue",
  InTransit: "cyan",
  NearExpiry: "orange",
  ExtensionWindowOpen: "gold",
  Extended: "geekblue",
  Delivered: "green",
  Cancelled: "default",
  Expired: "red",
  Error: "volcano",
  ManualActionRequired: "red",
};

export const EWayBillDashboardPage = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState<EWBDashboardStats | null>(null);
  const [rows, setRows] = useState<EWayBillListItemDto[]>([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [environment, setEnvironment] = useState<EWBEnvironment | null>(null);

  // Details modal
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [selectedRow, setSelectedRow] = useState<EWayBillListItemDto | null>(null);

  // Regenerate modal
  const [regenerateOpen, setRegenerateOpen] = useState(false);
  const [regenerating, setRegenerating] = useState(false);
  const [regenerateForm] = Form.useForm();

  // Manual Extend modal
  const [extendOpen, setExtendOpen] = useState(false);
  const [extending, setExtending] = useState(false);
  const [extendForm] = Form.useForm();

  // GPS Extend modal
  const [gpsExtendOpen, setGpsExtendOpen] = useState(false);
  const [gpsExtending, setGpsExtending] = useState(false);
  const [gpsExtendForm] = Form.useForm();

  // Update Vehicle modal
  const [updateVehicleOpen, setUpdateVehicleOpen] = useState(false);
  const [updatingVehicle, setUpdatingVehicle] = useState(false);
  const [updateVehicleForm] = Form.useForm();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [dashboard, list, env] = await Promise.all([
        transportApi.getEWayBillDashboard(),
        transportApi.listEWayBills({ page: 1, pageSize: 100 }),
        transportApi.getEWayBillEnvironment(),
      ]);
      setStats(dashboard);
      setRows(list.items ?? []);
      setEnvironment(env);
    } catch (err) {
      console.error(getApiErrorMessage(err, "Failed to load EWB dashboard"));
    } finally {
      setLoading(false);
    }
  }, []);

  const { connected, alerts, clearAlerts } = useEWBNotifications(() => void load());

  useEffect(() => { void load(); }, [load]);

  // ── Actions ───────────────────────────────────────────────────────────

  const openConsignmentFlow = () => {
    if (!selectedRow) return;
    navigate(`/consignments?consignmentId=${selectedRow.consignmentId}&openEwb=1`);
  };

  const openRegenerateModal = (row: EWayBillListItemDto) => {
    setSelectedRow(row);
    regenerateForm.setFieldsValue({
      eWayBillNo: "",
      generatedOn: dayjs(),
      validUpto: dayjs().add(1, "day"),
      currentVehicleNo: row.lastKnownVehicleNo ?? undefined,
      currentFromPlace: row.lastKnownFromPlace ?? row.origin,
      remainingDistanceKm: row.remainingDistanceKm ?? undefined,
    });
    setRegenerateOpen(true);
  };

  const openExtendModal = (row: EWayBillListItemDto) => {
    setSelectedRow(row);
    extendForm.setFieldsValue({
      vehicleNo: row.lastKnownVehicleNo ?? "",
      fromPlace: row.lastKnownFromPlace ?? "",
      remainingDistanceKm: row.remainingDistanceKm ?? 100,
      reasonCode: 4,
      remarks: "",
    });
    setExtendOpen(true);
  };

  const openGpsExtendModal = (row: EWayBillListItemDto) => {
    setSelectedRow(row);
    gpsExtendForm.setFieldsValue({
      vehicleNo: row.lastKnownVehicleNo ?? "",
      fromPlace: row.lastKnownFromPlace ?? "",
      latitude: "",
      longitude: "",
    });
    setGpsExtendOpen(true);
  };

  const openUpdateVehicleModal = (row: EWayBillListItemDto) => {
    setSelectedRow(row);
    updateVehicleForm.setFieldsValue({
      newVehicleNo: "",
      fromPlace: row.lastKnownFromPlace ?? "",
      transportMode: 1,
      remarks: "",
    });
    setUpdateVehicleOpen(true);
  };

  // ── Submit handlers ───────────────────────────────────────────────────

  const onRegenerate = async () => {
    if (!selectedRow) return;
    const values = await regenerateForm.validateFields();
    const ctx = getDefaultContext();
    setRegenerating(true);
    try {
      await transportApi.generateEWayBill({
        organizationId: ctx.organizationId,
        financialYearId: ctx.financialYearId,
        consignmentId: selectedRow.consignmentId,
        eWayBillNo: values.eWayBillNo,
        generatedOn: values.generatedOn.toISOString(),
        validUpto: values.validUpto.toISOString(),
        generatedBy: getEnteredByUser(),
        transportMode: "Road",
        currentVehicleNo: values.currentVehicleNo,
        currentFromPlace: values.currentFromPlace,
        remainingDistanceKm: values.remainingDistanceKm,
      });
      message.success("Expired E-Way Bill regenerated successfully");
      setRegenerateOpen(false);
      regenerateForm.resetFields();
      await load();
    } catch (err) {
      message.error(getApiErrorMessage(err, "Unable to regenerate E-Way Bill"));
    } finally {
      setRegenerating(false);
    }
  };

  const onExtend = async () => {
    if (!selectedRow) return;
    const values = await extendForm.validateFields();
    setExtending(true);
    try {
      const result = await transportApi.extendEWayBill(selectedRow.id, {
        vehicleNo: values.vehicleNo,
        fromPlace: values.fromPlace,
        remainingDistanceKm: values.remainingDistanceKm,
        reasonCode: values.reasonCode,
        remarks: values.remarks,
        extendedBy: getEnteredByUser(),
      });
      message.success(`E-Way Bill extended successfully. New expiry: ${dayjs(result.validUpto).format("DD MMM YYYY HH:mm")}`);
      setExtendOpen(false);
      extendForm.resetFields();
      await load();
    } catch (err) {
      message.error(getApiErrorMessage(err, "Unable to extend E-Way Bill"));
    } finally {
      setExtending(false);
    }
  };

  const onGpsExtend = async () => {
    if (!selectedRow) return;
    const values = await gpsExtendForm.validateFields();
    setGpsExtending(true);
    try {
      const result = await transportApi.extendEWayBillByGps(selectedRow.id, {
        latitude: parseFloat(values.latitude),
        longitude: parseFloat(values.longitude),
        vehicleNo: values.vehicleNo,
        fromPlace: values.fromPlace || undefined,
        extendedBy: getEnteredByUser(),
      });
      message.success(`GPS Extension successful. New expiry: ${dayjs(result.validUpto).format("DD MMM YYYY HH:mm")}`);
      setGpsExtendOpen(false);
      gpsExtendForm.resetFields();
      await load();
    } catch (err) {
      message.error(getApiErrorMessage(err, "Unable to extend E-Way Bill via GPS"));
    } finally {
      setGpsExtending(false);
    }
  };

  const onUpdateVehicle = async () => {
    if (!selectedRow) return;
    const values = await updateVehicleForm.validateFields();
    setUpdatingVehicle(true);
    try {
      await transportApi.updateEWayBillVehicle(selectedRow.id, {
        newVehicleNo: values.newVehicleNo,
        fromPlace: values.fromPlace,
        transportMode: values.transportMode,
        remarks: values.remarks,
        updatedBy: getEnteredByUser(),
      });
      message.success("Vehicle updated on E-Way Bill successfully");
      setUpdateVehicleOpen(false);
      updateVehicleForm.resetFields();
      await load();
    } catch (err) {
      message.error(getApiErrorMessage(err, "Unable to update vehicle on E-Way Bill"));
    } finally {
      setUpdatingVehicle(false);
    }
  };

  // ── Filtered rows ─────────────────────────────────────────────────────

  const filteredRows = rows.filter((row) => {
    const term = search.trim().toLowerCase();
    if (!term) return true;
    return [row.eWayBillNo, row.consignmentNo ?? "", row.origin, row.destination, row.currentStatus]
      .some((v) => v.toLowerCase().includes(term));
  });

  // ── Render ────────────────────────────────────────────────────────────

  return (
    <Card className="panel-card">
      <div className="panel-title-row">
        <Space align="center" size={12}>
          <Typography.Title level={3} style={{ margin: 0 }}>E-Way Bill Dashboard</Typography.Title>
          {environment && (
            <Tooltip placement="bottom" title={`NIC API: ${environment.baseUrl}`}>
              <Tag color={environment.isSandbox ? "orange" : "green"} style={{ fontWeight: 600 }}>
                {environment.isSandbox ? "⚠ SANDBOX" : "✓ PRODUCTION"}
              </Tag>
            </Tooltip>
          )}
        </Space>
        <Space>
          <Badge status={connected ? "success" : "default"} text={connected ? "Live" : "Offline"} />
          <Button size="small" onClick={() => void load()} loading={loading}>
            Refresh
          </Button>
          {alerts.length > 0 && (
            <Button size="small" danger onClick={clearAlerts}>
              Clear {alerts.length} Alert{alerts.length > 1 ? "s" : ""}
            </Button>
          )}
        </Space>
      </div>

      {environment?.isSandbox && (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
          message="Sandbox Mode Active"
          description={`Connected to NIC EWB Sandbox (${environment.baseUrl}). All API calls are test-only and will not affect real E-Way Bills. Switch to production in appsettings.json.`}
        />
      )}

      <Row gutter={[12, 12]} style={{ marginBottom: 20 }}>
        <Col xs={12} sm={8} md={8} lg={4}>
          <Card size="small">
            <Statistic title="Active EWBs" value={stats?.totalActive ?? "-"} prefix={<SyncOutlined style={{ color: "#1677ff" }} />} loading={loading} />
          </Card>
        </Col>
        <Col xs={12} sm={8} md={8} lg={4}>
          <Card size="small">
            <Statistic title="Expiring in 8h" value={stats?.expiringIn8Hours ?? "-"} prefix={<ClockCircleOutlined style={{ color: "#fa8c16" }} />} valueStyle={{ color: (stats?.expiringIn8Hours ?? 0) > 0 ? "#fa8c16" : undefined }} loading={loading} />
          </Card>
        </Col>
        <Col xs={12} sm={8} md={8} lg={4}>
          <Card size="small">
            <Statistic title="Extension Window Open" value={stats?.extensionWindowOpen ?? "-"} prefix={<WarningOutlined style={{ color: "#faad14" }} />} loading={loading} />
          </Card>
        </Col>
        <Col xs={12} sm={8} md={8} lg={4}>
          <Card size="small">
            <Statistic title="Manual Action Required" value={stats?.manualActionRequired ?? "-"} prefix={<ExclamationCircleOutlined style={{ color: "#ff4d4f" }} />} valueStyle={{ color: (stats?.manualActionRequired ?? 0) > 0 ? "#ff4d4f" : undefined }} loading={loading} />
          </Card>
        </Col>
        <Col xs={12} sm={8} md={8} lg={4}>
          <Card size="small">
            <Statistic title="Delivered" value={stats?.delivered ?? "-"} prefix={<CheckCircleOutlined style={{ color: "#52c41a" }} />} loading={loading} />
          </Card>
        </Col>
        <Col xs={12} sm={8} md={8} lg={4}>
          <Card size="small">
            <Statistic title="Expired" value={stats?.expired ?? "-"} prefix={<StopOutlined style={{ color: "#ff4d4f" }} />} valueStyle={{ color: (stats?.expired ?? 0) > 0 ? "#ff4d4f" : undefined }} loading={loading} />
          </Card>
        </Col>
      </Row>

      {alerts.length > 0 && (
        <Card title="Recent Alerts (Live)" size="small" style={{ marginBottom: 24 }} extra={<Button size="small" onClick={clearAlerts}>Clear All</Button>}>
          <Table
            rowKey={(r, i) => `${r.eWayBillId}-${i}`}
            dataSource={alerts.slice(0, 20)}
            size="small"
            pagination={false}
            columns={[
              { title: "EWB No", dataIndex: "eWayBillNo", width: 160, render: (v: string) => <Typography.Text code>{v}</Typography.Text> },
              { title: "Alert", dataIndex: "alertType", width: 200, render: (v: string) => <Tag>{v}</Tag> },
              {
                title: "Severity",
                dataIndex: "severityLevel",
                width: 100,
                render: (v: string) => {
                  const colorMap: Record<string, string> = { info: "blue", warning: "orange", error: "red", critical: "red" };
                  return <Tag color={colorMap[v] ?? "default"}>{v.toUpperCase()}</Tag>;
                },
              },
              { title: "Message", dataIndex: "message" },
              { title: "When", dataIndex: "occurredAt", width: 120, render: (v: string) => dayjs(v).fromNow() },
            ]}
          />
        </Card>
      )}

      <Card title="Tracked E-Way Bills" size="small" style={{ marginBottom: 16 }}>
        <Space style={{ width: "100%", justifyContent: "space-between", marginBottom: 12 }} wrap>
          <Input
            prefix={<SearchOutlined />}
            placeholder="Search EWB no, consignment, route, status..."
            allowClear
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{ width: 360 }}
          />
          <Typography.Text type="secondary">
            Showing {filteredRows.length} of {rows.length} loaded E-Way Bills
          </Typography.Text>
        </Space>
        <Table
          rowKey="id"
          loading={loading}
          dataSource={filteredRows}
          size="small"
          locale={{ emptyText: <Empty description="No tracked E-Way Bills found" /> }}
          pagination={{
            current: page,
            pageSize,
            total: filteredRows.length,
            showSizeChanger: true,
            pageSizeOptions: ["25", "50", "100"],
            showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} E-Way Bills`,
            onChange: (nextPage, nextPageSize) => { setPage(nextPage); setPageSize(nextPageSize); }
          }}
          columns={[
            { title: "#", width: 70, render: (_, __, index) => ((page - 1) * pageSize) + index + 1 },
            { title: "EWB No", dataIndex: "eWayBillNo", width: 160, render: (v: string) => <Typography.Text code>{v}</Typography.Text> },
            { title: "Consignment", render: (_, row) => row.consignmentNo ?? `CN-${row.consignmentId}` },
            { title: "Route", render: (_, row) => `${row.origin} → ${row.destination}` },
            { title: "Status", dataIndex: "currentStatus", width: 160, render: (v: string) => <Tag color={STATUS_COLORS[v] ?? "default"}>{v}</Tag> },
            {
              title: "Valid Upto",
              dataIndex: "validUpto",
              width: 150,
              render: (v: string) => (
                <span style={{ color: dayjs(v).isBefore(dayjs()) ? "#ff4d4f" : undefined }}>
                  {dayjs(v).format("DD MMM YYYY HH:mm")}
                </span>
              ),
            },
            {
              title: "Actions",
              width: 220,
              render: (_, row) => (
                <Space size={4} wrap>
                  <Button size="small" onClick={() => { setSelectedRow(row); setDetailsOpen(true); }}>Details</Button>
                  {row.currentStatus === "Expired" && (
                    <Button size="small" type="primary" ghost onClick={() => openRegenerateModal(row)}>
                      Regenerate
                    </Button>
                  )}
                  {!row.isCancelled && row.currentStatus !== "Expired" && row.currentStatus !== "Delivered" && (
                    <>
                      <Tooltip placement="bottom" title="Extend validity manually">
                        <Button size="small" icon={<ExpandAltOutlined />} onClick={() => openExtendModal(row)} />
                      </Tooltip>
                      <Tooltip placement="bottom" title="Extend via GPS coordinates">
                        <Button size="small" icon={<EnvironmentOutlined />} onClick={() => openGpsExtendModal(row)} />
                      </Tooltip>
                      <Tooltip placement="bottom" title="Update vehicle">
                        <Button size="small" icon={<CarOutlined />} onClick={() => openUpdateVehicleModal(row)} />
                      </Tooltip>
                    </>
                  )}
                </Space>
              ),
            },
          ]}
        />
      </Card>

      <Typography.Text type="secondary" style={{ fontSize: 12 }}>
        Dashboard auto-refreshes on live E-Way Bill events via SignalR.
        {!connected && " (Real-time updates offline — check connection)"}
      </Typography.Text>

      {/* ── Details Modal ───────────────────────────────────────────── */}
      <StaticModal
        title={selectedRow ? `E-Way Bill Details — ${selectedRow.eWayBillNo}` : "E-Way Bill Details"}
        open={detailsOpen}
        onCancel={() => { setDetailsOpen(false); setSelectedRow(null); }}
        footer={null}
        width={760}
        destroyOnHidden
        maskClosable={false}
        keyboard={false}
      >
        {!selectedRow ? (
          <Empty description="Select an E-Way Bill to inspect details" />
        ) : (
          <Space direction="vertical" style={{ width: "100%" }}>
            <Descriptions bordered column={2} size="small">
              <Descriptions.Item label="EWB No">{selectedRow.eWayBillNo}</Descriptions.Item>
              <Descriptions.Item label="Consignment">{selectedRow.consignmentNo ?? `CN-${selectedRow.consignmentId}`}</Descriptions.Item>
              <Descriptions.Item label="Origin">{selectedRow.origin}</Descriptions.Item>
              <Descriptions.Item label="Destination">{selectedRow.destination}</Descriptions.Item>
              <Descriptions.Item label="Status"><Tag color={STATUS_COLORS[selectedRow.currentStatus] ?? "default"}>{selectedRow.currentStatus}</Tag></Descriptions.Item>
              <Descriptions.Item label="Auto Extension">{selectedRow.autoExtendEnabled ? "Enabled" : "Stopped"}</Descriptions.Item>
              <Descriptions.Item label="Generated On">{dayjs(selectedRow.generatedOn).format("DD MMM YYYY HH:mm")}</Descriptions.Item>
              <Descriptions.Item label="Valid Upto">{dayjs(selectedRow.validUpto).format("DD MMM YYYY HH:mm")}</Descriptions.Item>
              <Descriptions.Item label="Generated By">{selectedRow.generatedBy}</Descriptions.Item>
              <Descriptions.Item label="Remaining Distance">{selectedRow.remainingDistanceKm != null ? `${selectedRow.remainingDistanceKm} km` : "—"}</Descriptions.Item>
              <Descriptions.Item label="Vehicle No">{selectedRow.lastKnownVehicleNo ?? "—"}</Descriptions.Item>
              <Descriptions.Item label="Current Place">{selectedRow.lastKnownFromPlace ?? "—"}</Descriptions.Item>
              <Descriptions.Item label="Last API Message" span={2}>{selectedRow.lastApiErrorMessage ?? "—"}</Descriptions.Item>
            </Descriptions>
            <Space wrap>
              {selectedRow.currentStatus === "Expired" && (
                <Button onClick={() => { setDetailsOpen(false); openRegenerateModal(selectedRow); }}>
                  Regenerate
                </Button>
              )}
              {!selectedRow.isCancelled && selectedRow.currentStatus !== "Expired" && selectedRow.currentStatus !== "Delivered" && (
                <>
                  <Button icon={<ExpandAltOutlined />} onClick={() => { setDetailsOpen(false); openExtendModal(selectedRow); }}>
                    Extend Manually
                  </Button>
                  <Button icon={<EnvironmentOutlined />} onClick={() => { setDetailsOpen(false); openGpsExtendModal(selectedRow); }}>
                    Extend via GPS
                  </Button>
                  <Button icon={<CarOutlined />} onClick={() => { setDetailsOpen(false); openUpdateVehicleModal(selectedRow); }}>
                    Update Vehicle
                  </Button>
                </>
              )}
              <Button type="primary" onClick={openConsignmentFlow}>
                Open In Consignments
              </Button>
              <Button onClick={() => navigate(`/consignments?consignmentId=${selectedRow.consignmentId}`)}>
                Open Consignment List
              </Button>
            </Space>
          </Space>
        )}
      </StaticModal>

      {/* ── Regenerate Modal ────────────────────────────────────────── */}
      <StaticModal
        title={selectedRow ? `Regenerate E-Way Bill — ${selectedRow.eWayBillNo}` : "Regenerate E-Way Bill"}
        open={regenerateOpen}
        onCancel={() => { setRegenerateOpen(false); regenerateForm.resetFields(); }}
        footer={null}
        width={640}
        destroyOnHidden
        maskClosable={false}
      >
        <Form form={regenerateForm} layout="vertical">
          <Form.Item name="eWayBillNo" label="New E-Way Bill No" rules={[{ required: true, message: "Enter the regenerated E-Way Bill number" }]}>
            <Input />
          </Form.Item>
          <Row gutter={12}>
            <Col span={12}>
              <Form.Item name="generatedOn" label="Generated On" rules={[{ required: true }]}>
                <DatePicker showTime style={{ width: "100%" }} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="validUpto" label="Valid Upto" rules={[{ required: true }]}>
                <DatePicker showTime style={{ width: "100%" }} />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={12}>
            <Col span={12}>
              <Form.Item name="currentVehicleNo" label="Current Vehicle No">
                <Input />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="currentFromPlace" label="Current From Place">
                <Input />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item name="remainingDistanceKm" label="Remaining Distance (km)">
            <InputNumber style={{ width: "100%" }} min={0} />
          </Form.Item>
          <Space style={{ width: "100%", justifyContent: "flex-end" }}>
            <Button onClick={() => { setRegenerateOpen(false); regenerateForm.resetFields(); }}>Close</Button>
            <SaveActionButton saving={regenerating} idleText="Regenerate" savingText="Regenerating..." onClick={() => void onRegenerate()} />
          </Space>
        </Form>
      </StaticModal>

      {/* ── Manual Extend Modal ─────────────────────────────────────── */}
      <StaticModal
        title={selectedRow ? `Extend E-Way Bill — ${selectedRow.eWayBillNo}` : "Extend E-Way Bill"}
        open={extendOpen}
        onCancel={() => { setExtendOpen(false); extendForm.resetFields(); }}
        footer={null}
        width={580}
        destroyOnHidden
        maskClosable={false}
      >
        <Typography.Text type="secondary" style={{ display: "block", marginBottom: 16 }}>
          Submit an extension request to NIC with the current vehicle location and remaining distance.
        </Typography.Text>
        <Form form={extendForm} layout="vertical">
          <Row gutter={12}>
            <Col span={12}>
              <Form.Item name="vehicleNo" label="Vehicle No" rules={[{ required: true, message: "Vehicle number is required" }]}>
                <Input placeholder="e.g. MH12AB1234" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="fromPlace" label="Current Place / Location" rules={[{ required: true, message: "Current place is required" }]}>
                <Input placeholder="e.g. Nashik" />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={12}>
            <Col span={12}>
              <Form.Item name="remainingDistanceKm" label="Remaining Distance (km)" rules={[{ required: true, message: "Enter remaining distance" }]}>
                <InputNumber style={{ width: "100%" }} min={1} max={9999} placeholder="e.g. 250" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="reasonCode" label="Extension Reason" rules={[{ required: true }]}>
                <Select options={EXTENSION_REASON_OPTIONS} />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item name="remarks" label="Remarks (optional)">
            <Input.TextArea rows={2} placeholder="Additional remarks sent to NIC" />
          </Form.Item>
          <Space style={{ width: "100%", justifyContent: "flex-end" }}>
            <Button onClick={() => { setExtendOpen(false); extendForm.resetFields(); }}>Cancel</Button>
            <SaveActionButton saving={extending} idleText="Extend via NIC" savingText="Extending..." onClick={() => void onExtend()} />
          </Space>
        </Form>
      </StaticModal>

      {/* ── GPS Extend Modal ────────────────────────────────────────── */}
      <StaticModal
        title={selectedRow ? `GPS Extension — ${selectedRow.eWayBillNo}` : "GPS Extension"}
        open={gpsExtendOpen}
        onCancel={() => { setGpsExtendOpen(false); gpsExtendForm.resetFields(); }}
        footer={null}
        width={540}
        destroyOnHidden
        maskClosable={false}
      >
        <Typography.Text type="secondary" style={{ display: "block", marginBottom: 16 }}>
          Provide the vehicle's current GPS coordinates. The system will calculate the remaining
          distance and submit an extension to NIC with reason code <strong>5 (In Transit Cargo)</strong>.
        </Typography.Text>
        <Form form={gpsExtendForm} layout="vertical">
          <Row gutter={12}>
            <Col span={12}>
              <Form.Item
                name="latitude"
                label="Latitude"
                rules={[
                  { required: true, message: "Latitude is required" },
                  { pattern: /^-?\d+(\.\d+)?$/, message: "Enter a valid decimal latitude" },
                ]}
              >
                <Input placeholder="e.g. 19.9975" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="longitude"
                label="Longitude"
                rules={[
                  { required: true, message: "Longitude is required" },
                  { pattern: /^-?\d+(\.\d+)?$/, message: "Enter a valid decimal longitude" },
                ]}
              >
                <Input placeholder="e.g. 73.7898" />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={12}>
            <Col span={12}>
              <Form.Item name="vehicleNo" label="Vehicle No" rules={[{ required: true, message: "Vehicle number is required" }]}>
                <Input placeholder="e.g. MH12AB1234" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="fromPlace" label="Place Name (optional)">
                <Input placeholder="Resolved from GPS or enter manually" />
              </Form.Item>
            </Col>
          </Row>
          <Space style={{ width: "100%", justifyContent: "flex-end" }}>
            <Button onClick={() => { setGpsExtendOpen(false); gpsExtendForm.resetFields(); }}>Cancel</Button>
            <SaveActionButton saving={gpsExtending} idleText="Extend via GPS" savingText="Extending..." onClick={() => void onGpsExtend()} />
          </Space>
        </Form>
      </StaticModal>

      {/* ── Update Vehicle Modal ────────────────────────────────────── */}
      <StaticModal
        title={selectedRow ? `Update Vehicle — ${selectedRow.eWayBillNo}` : "Update Vehicle"}
        open={updateVehicleOpen}
        onCancel={() => { setUpdateVehicleOpen(false); updateVehicleForm.resetFields(); }}
        footer={null}
        width={540}
        destroyOnHidden
        maskClosable={false}
      >
        <Typography.Text type="secondary" style={{ display: "block", marginBottom: 16 }}>
          Update the vehicle number on an active E-Way Bill (e.g. due to transshipment or vehicle change).
          {selectedRow?.lastKnownVehicleNo && (
            <> Current vehicle: <strong>{selectedRow.lastKnownVehicleNo}</strong></>
          )}
        </Typography.Text>
        <Form form={updateVehicleForm} layout="vertical">
          <Row gutter={12}>
            <Col span={12}>
              <Form.Item name="newVehicleNo" label="New Vehicle No" rules={[{ required: true, message: "New vehicle number is required" }]}>
                <Input placeholder="e.g. MH12CD5678" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="fromPlace" label="Transshipment Place" rules={[{ required: true, message: "Place is required" }]}>
                <Input placeholder="e.g. Pune" />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={12}>
            <Col span={12}>
              <Form.Item name="transportMode" label="Transport Mode" rules={[{ required: true }]}>
                <Select
                  options={[
                    { value: 1, label: "1 – Road" },
                    { value: 2, label: "2 – Rail" },
                    { value: 3, label: "3 – Air" },
                    { value: 4, label: "4 – Ship" },
                  ]}
                />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item name="remarks" label="Remarks (optional)">
            <Input.TextArea rows={2} placeholder="Reason for vehicle change" />
          </Form.Item>
          <Space style={{ width: "100%", justifyContent: "flex-end" }}>
            <Button onClick={() => { setUpdateVehicleOpen(false); updateVehicleForm.resetFields(); }}>Cancel</Button>
            <SaveActionButton saving={updatingVehicle} idleText="Update Vehicle" savingText="Updating..." onClick={() => void onUpdateVehicle()} />
          </Space>
        </Form>
      </StaticModal>
    </Card>
  );
};
